import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import CalendlyModal from '../components/CalendlyModal';

const SoftwareRejectionThankYou: React.FC = () => {
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);

  const handleCalendlyClose = () => {
    setIsCalendlyModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircle className="h-8 w-8 text-red-600" />
          </motion.div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Thank You for Your Time
          </h1>
          
          <div className="w-24 h-1 bg-red-600 mx-auto rounded-full"></div>
        </div>

        {/* Main Content */}
        <div className="space-y-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg"
          >
            <h2 className="text-xl font-bold text-blue-800 mb-3">
              Important Clarification
            </h2>
            <p className="text-blue-700 leading-relaxed">
              We are <strong>software providers</strong>, not a digital marketing agency to provide leads. 
              Our focus is on providing salon management and growth automation software solutions.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg"
          >
            <h2 className="text-xl font-bold text-green-800 mb-3">
              Interested in Growing Your Business?
            </h2>
            <p className="text-green-700 leading-relaxed mb-4">
              If you are interested in growing your business through software solutions, 
              we invite you to visit our website and explore our salon management software.
            </p>
            
            <button
              onClick={() => setIsCalendlyModalOpen(true)}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              <ExternalLink className="h-5 w-5" />
              Still You Want To Book Demo
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gray-50 border-l-4 border-gray-400 p-6 rounded-r-lg"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              What You Can Expect:
            </h3>
            <ul className="text-gray-700 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Complete salon management software
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Growth automation tools
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                24/7 automated booking system
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Revenue growth solutions
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link
            to="/revenue"
            className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Revenue Page
          </Link>
          
          <a
            href="https://zylu.co/pricing-salon-growth-management-software/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            <ExternalLink className="h-5 w-5" />
            Purchase Software
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-8 pt-6 border-t border-gray-200"
        >
          <p className="text-gray-500 text-sm">
            Thank you for considering Zylu for your salon growth needs.
          </p>
        </motion.div>
      </motion.div>

      {/* Calendly Modal */}
      <CalendlyModal
        isOpen={isCalendlyModalOpen}
        onClose={handleCalendlyClose}
        url="https://calendly.com/zylusales/group-growth-consultation?hide_gdpr_banner=1"
        title="Schedule Your Free Consultation"
      />
    </div>
  );
};

export default SoftwareRejectionThankYou;
